<template>
  <!--
    CustomerNoteEditorDialog
    ========================

    A separate dialog (renders on top of any parent dialog) used to
    create or edit ONE customer note + its attachments.

    Why "separate dialog" and not an inline form?
      A previous attempt used an inline form inside the parent dialog
      with a v-model pointing at a parent reactive object. That setup
      created a feedback loop in Vue's reactivity scheduler — every
      keystroke re-emitted the model, which re-assigned files = [...],
      which re-triggered the deep watch, etc. The whole parent dialog
      locked up. Splitting the editor into its own dialog instance
      solves this completely: the editor's input state is purely local
      (refs to primitives), the result is delivered ONCE via @saved,
      and any catastrophic failure inside the editor cannot affect
      the parent dialog.

    Important non-goals (kept out on purpose):
      * No API calls from this component. We only emit `saved`; the
        parent decides whether to POST/PATCH (saved mode) or push to
        a local draft list (NewCustomerDialog draft mode).
      * No `auto-grow` on the textarea — Vuetify 3 collapses height
        to 0 with `auto-grow + hide-details`. We use fixed `rows`.
  -->
  <v-dialog
    :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
    max-width="560"
    persistent
  >
    <v-card v-if="modelValue">
      <v-card-title class="d-flex align-center pa-3">
        <v-icon size="20" class="mr-2" color="primary">
          {{ isEdit ? 'mdi-note-edit-outline' : 'mdi-note-plus-outline' }}
        </v-icon>
        {{ isEdit ? 'Edit note' : 'Add note' }}
        <v-spacer />
        <v-btn icon size="small" variant="text" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text class="pa-3">
        <v-textarea
          v-model="content"
          placeholder="Type your note here…"
          variant="outlined"
          rows="5"
          no-resize
          hide-details="auto"
          autofocus
          class="mb-3"
        />

        <!--
          Existing attachments (only relevant when editing a saved note).
          User can mark them for removal — the parent will actually call
          the API after Save.
        -->
        <div v-if="visibleExistingAttachments.length" class="mb-2">
          <div class="text-caption text-medium-emphasis mb-1">
            Currently attached:
          </div>
          <div class="d-flex flex-wrap ga-2">
            <v-chip
              v-for="att in visibleExistingAttachments"
              :key="`e-${att.id}`"
              size="small"
              variant="tonal"
              color="primary"
              closable
              @click:close="markAttachmentForRemoval(att)"
            >
              <v-icon start size="14">mdi-paperclip</v-icon>
              {{ att.file_name }} · {{ formatBytes(att.file_size) }}
            </v-chip>
          </div>
        </div>

        <!-- Newly picked files (not uploaded yet). -->
        <div v-if="files.length" class="mb-2">
          <div class="text-caption text-medium-emphasis mb-1">
            To upload:
          </div>
          <div class="d-flex flex-wrap ga-2">
            <v-chip
              v-for="(f, i) in files"
              :key="`n-${i}`"
              size="small"
              variant="tonal"
              color="accent"
              closable
              @click:close="files.splice(i, 1)"
            >
              <v-icon start size="14">mdi-paperclip</v-icon>
              {{ f.name }} · {{ formatBytes(f.size) }}
            </v-chip>
          </div>
        </div>

        <v-btn
          size="small"
          variant="outlined"
          color="primary"
          @click="triggerFilePicker"
        >
          <v-icon start size="16">mdi-paperclip</v-icon>
          Attach files
        </v-btn>
        <input
          ref="fileInputRef"
          type="file"
          multiple
          style="display:none"
          @change="onFilePicked"
        >
      </v-card-text>

      <v-divider />

      <v-card-actions class="pa-3">
        <v-spacer />
        <v-btn variant="text" @click="close">Cancel</v-btn>
        <v-btn
          color="accent"
          :disabled="!content.trim()"
          :loading="saving"
          @click="save"
        >
          {{ isEdit ? 'Save changes' : 'Save note' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useNotify } from '@/composables/useNotify'

const props = defineProps({
  /** Two-way bound: opens / closes the dialog. */
  modelValue: { type: Boolean, default: false },
  /**
   * Frozen payload describing what to show. Provided by the parent
   * every time the dialog is opened. We copy it into local refs on
   * open and never mutate the prop.
   *
   *   { type: 'create' | 'saved' | 'draft',
   *     content?: string,
   *     files?: File[],
   *     existingAttachments?: AttachmentRow[] }
   */
  initialNote: { type: Object, default: null },
})

const emit = defineEmits(['update:modelValue', 'saved'])

const { notify } = useNotify()

// Local input state — primitive refs, no reactive parent objects.
const content = ref('')
const files = ref([])
const existingAttachments = ref([])
const removedAttachmentIds = ref([])
const saving = ref(false)
const fileInputRef = ref(null)

const isEdit = computed(() => {
  const t = props.initialNote?.type
  return t === 'saved' || t === 'draft'
})

const visibleExistingAttachments = computed(() =>
  existingAttachments.value.filter(
    (a) => !removedAttachmentIds.value.includes(a.id),
  ),
)

// Re-initialize state every time the dialog opens.
watch(() => props.modelValue, (open) => {
  if (!open) return
  content.value = props.initialNote?.content || ''
  files.value = Array.isArray(props.initialNote?.files)
    ? [...props.initialNote.files]
    : []
  existingAttachments.value = Array.isArray(props.initialNote?.existingAttachments)
    ? [...props.initialNote.existingAttachments]
    : []
  removedAttachmentIds.value = []
  saving.value = false
})

function close() {
  emit('update:modelValue', false)
}

function triggerFilePicker() {
  fileInputRef.value?.click()
}

function onFilePicked(ev) {
  const list = Array.from(ev.target?.files || [])
  // 20 MB hard cap — backend enforces too, this is only a friendly
  // heads-up so the user doesn't waste time uploading and getting 413.
  const MAX = 20 * 1024 * 1024
  for (const f of list) {
    if (f.size > MAX) {
      notify.warning(`File "${f.name}" is larger than 20 MB and was skipped.`)
      continue
    }
    files.value.push(f)
  }
  // Reset the file input so the user can re-pick the same name later.
  if (ev.target) ev.target.value = ''
}

function markAttachmentForRemoval(att) {
  removedAttachmentIds.value.push(att.id)
}

function save() {
  if (!content.value.trim()) return
  saving.value = true
  try {
    emit('saved', {
      content: content.value.trim(),
      files: [...files.value],
      existingAttachmentIdsToRemove: [...removedAttachmentIds.value],
    })
    close()
  } finally {
    saving.value = false
  }
}

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
</script>
